import api from '../api';

/** Base path shared by the real API and the B1a development stub. */
export const ANYWHERE_API_PATH = '/anywhere';

export const normalizeAnywhereBasePath = (basePath = ANYWHERE_API_PATH) => {
  const normalized = String(basePath).replace(/\/+$/, '');
  return normalized || ANYWHERE_API_PATH;
};

/**
 * Wire value defined by the API contract for a tenant without `id_anywhere`.
 *
 * The English alias is intentional: screens should describe the condition, while
 * the value remains exactly the Portuguese protocol value returned by the API.
 */
export const ANYWHERE_TENANT_SEM_VINCULO = 'ANYWHERE_TENANT_SEM_VINCULO';
export const ANYWHERE_TENANT_NOT_LINKED = ANYWHERE_TENANT_SEM_VINCULO;

/**
 * Normalized Anywhere error. Axios normally exposes a response only for failed
 * HTTP statuses; turning it into this shape keeps screen code independent of
 * Axios and, most importantly, lets it distinguish the tenant-link 409 from a
 * transient service error.
 */
export class AnywhereApiError extends Error {
  constructor({ message, status, code, errors, response, cause }) {
    super(message);
    this.name = 'AnywhereApiError';
    this.status = status;
    this.code = code;
    this.errors = errors;
    this.response = response;
    this.cause = cause;
  }
}

const isAnywhereEnvelope = (value) =>
  value && typeof value === 'object' && (value.status === 'success' || value.status === 'error');

const errorFromResponse = (response, cause) => {
  const envelope = response?.data;
  const errors = Array.isArray(envelope?.data?.errors) ? envelope.data.errors : [];
  const message = errors[0] || cause?.message || 'Não foi possível concluir a operação no Anywhere.';

  return new AnywhereApiError({
    message,
    status: response?.status,
    code: envelope?.data?.code,
    errors,
    response,
    cause,
  });
};

/** Converts both Axios failures and an unexpected error envelope to one shape. */
export const toAnywhereApiError = (error) => {
  if (error instanceof AnywhereApiError) return error;
  return errorFromResponse(error?.response, error);
};

/** True only for the actionable 409 where the dashboard tenant lacks an Anywhere link. */
export const isAnywhereTenantNotLinkedError = (error) => {
  const normalized = toAnywhereApiError(error);
  return normalized.status === 409 && normalized.code === ANYWHERE_TENANT_SEM_VINCULO;
};

/** A display message for the operator; consumers must never compare this text. */
export const getAnywhereErrorMessage = (error) => toAnywhereApiError(error).message;

const withoutUndefined = (params) => {
  if (!params) return undefined;
  return Object.entries(params).reduce((result, [key, value]) => {
    if (value !== undefined) result[key] = value;
    return result;
  }, {});
};

/**
 * Makes a request to the Anywhere module and returns only the contract's
 * `data` payload. Success is always `{ status: 'success', data }`.
 */
export const anywhereRequest = async ({ method, path, basePath = ANYWHERE_API_PATH, params, data, ...config }) => {
  try {
    const response = await api.request({
      method,
      url: `${normalizeAnywhereBasePath(basePath)}${path}`,
      params: withoutUndefined(params),
      data,
      ...config,
    });

    if (response.data?.status === 'success') return response.data.data;
    if (isAnywhereEnvelope(response.data)) throw errorFromResponse(response);

    throw new AnywhereApiError({
      message: 'Resposta inválida recebida do módulo Anywhere.',
      status: response.status,
      response,
      errors: [],
    });
  } catch (error) {
    throw toAnywhereApiError(error);
  }
};

const filenameFromDisposition = (disposition, fallback) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(disposition || ''));
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const errorFromDownload = async (error) => {
  const blob = error?.response?.data;
  if (typeof Blob !== 'undefined' && blob instanceof Blob) {
    const text = await blob.text().catch(() => '');
    let envelope = null;
    try {
      envelope = JSON.parse(text);
    } catch {
      envelope = null;
    }
    if (isAnywhereEnvelope(envelope)) {
      return errorFromResponse({ ...error.response, data: envelope }, error);
    }
  }
  return toAnywhereApiError(error);
};

export const anywhereDownload = async ({
  method = 'get',
  path,
  basePath = ANYWHERE_API_PATH,
  params,
  fallbackFilename = 'arquivo',
}) => {
  try {
    const response = await api.request({
      method,
      url: `${normalizeAnywhereBasePath(basePath)}${path}`,
      params: withoutUndefined(params),
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename: filenameFromDisposition(
        response.headers?.['content-disposition'],
        fallbackFilename,
      ),
    };
  } catch (error) {
    throw await errorFromDownload(error);
  }
};
